/**
 * Enter moves to the next field instead of doing nothing.
 *
 * These forms are transcribed from paper, usually left to right and top to
 * bottom, often one-handed. Enter is what a person reaches for between fields,
 * and on a form with no submit button it otherwise does nothing at all — you
 * have to tab or tap the next box.
 *
 * Applied as a Svelte action to a container, so a form opts in once rather than
 * every input wiring up its own handler:
 *
 *     <div use:advanceOnEnter> ... </div>
 *
 * Deliberately NOT applied to textareas: Enter there means a new line, which is
 * exactly what someone typing a hazard description or a note wants. Shift+Enter
 * anywhere is left alone for the same reason, and so is Enter on a button or a
 * link, where it means "activate this".
 */

/** Focusable controls we will move between, in document order. */
const FOCUSABLE =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])';

function isEditableText(el: Element): boolean {
  return el.tagName === 'TEXTAREA';
}

export interface AdvanceOnEnterOptions {
  /**
   * Called when Enter is pressed on the LAST field in the container, instead of
   * wrapping around. A form can use this to move to its next section.
   */
  onLast?: () => void;
}

export function advanceOnEnter(node: HTMLElement, options: AdvanceOnEnterOptions = {}) {
  let opts = options;

  function handle(event: KeyboardEvent): void {
    if (event.key !== 'Enter' || event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) {
      return;
    }

    const target = event.target as HTMLElement | null;
    if (!target) return;

    // A newline is the right answer in a textarea, and Enter on a button or a
    // link already means something.
    if (isEditableText(target)) return;
    if (target.tagName === 'BUTTON' || target.tagName === 'A') return;
    if (!target.matches(FOCUSABLE)) return;

    const fields = [...node.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (el) => el.offsetParent !== null || el === target
    );
    const index = fields.indexOf(target);
    if (index === -1) return;

    // Enter would otherwise submit the surrounding form (or do nothing); either
    // way the user meant "next field".
    event.preventDefault();

    const next = fields[index + 1];
    if (next) {
      next.focus();
      if (next instanceof HTMLInputElement && next.type !== 'date' && next.select) next.select();
      return;
    }

    opts.onLast?.();
  }

  node.addEventListener('keydown', handle);

  return {
    update(next: AdvanceOnEnterOptions = {}) {
      opts = next;
    },
    destroy() {
      node.removeEventListener('keydown', handle);
    }
  };
}
